import { ItemTypeConsts } from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import LdapServerApi from 'client/api/v2/LdapServerApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import createModel from 'client/store/modules/models/createModel';

export const getDefaultItem = () => ({
  authenticationEnabled: false,
  id: '',
  ldapBindDns: [],
  name: 'WORKGROUP',
  sambaServiceUser: undefined,
  sid: '',
});

const model = createModel(getDefaultItem, 'id', LdapServerApi);

const generateSid = (orgId) => {
  // Default SID prefix.
  // SEE: https://technet.microsoft.com/en-us/library/cc962011.aspx
  let sid = 'S-1-2-21';

  for (let x = 0; x < orgId.length; x += 8) {
    const orgIdSlice = orgId.slice(x, x + 8);
    const val = `0000000000${parseInt(orgIdSlice, 16)}`;
    const bufferedVal = val.substr(val.length - 10);
    sid += (`-${bufferedVal}`);
  }

  return sid;
};

export default {
  namespaced: true,

  state: {
    ...model.state,
  },

  actions: {
    ...model.actions,

    async fetchItem({ commit, dispatch, rootState }, { ldapServerId }) {
      const sambaDomains = await LdapServerApi.getSambaDomains(ldapServerId);

      if (sambaDomains.length > 0) {
        commit('SET_ITEM', {
          authenticationEnabled: true,
          ...cloneDeep(sambaDomains[0]),
        });
        commit('SET_PREVIOUS_ITEM', {
          authenticationEnabled: true,
          ...cloneDeep(sambaDomains[0]),
        });
      } else {
        const sid = generateSid(rootState.SettingsModule.orgId);
        commit('SET_ITEM', { sid });
        commit('SET_PREVIOUS_ITEM', { sid });
      }

      await dispatch('fetchLdapBindDns');
      await dispatch('fetchSambaServiceUser');
    },

    async fetchLdapBindDns({ commit }) {
      const filters = [
        { name: 'activated', operator: 'eq', value: true },
        { name: 'ldap_binding_user', operator: 'eq', value: true },
      ];
      const ldapBindDns = await SystemUserApi.fetchAll(undefined, undefined, filters);

      commit('SET_ITEM', { ldapBindDns: cloneDeep(ldapBindDns) });
      commit('SET_PREVIOUS_ITEM', { ldapBindDns: cloneDeep(ldapBindDns) });
    },

    async fetchSambaServiceUser({ commit }) {
      const filters = [
        { name: 'samba_service_user', operator: 'eq', value: true },
      ];
      const sambaServiceUsers = await SystemUserApi.fetchAll(undefined, undefined, filters);

      if (sambaServiceUsers.length > 0) {
        commit('SET_ITEM', { sambaServiceUser: cloneDeep(sambaServiceUsers[0]) });
        commit('SET_PREVIOUS_ITEM', { sambaServiceUser: cloneDeep(sambaServiceUsers[0]) });
      }
    },

    async saveItem({ dispatch, state }, { ldapServerId }) {
      const { item, previousItem } = state;
      const actions = [];

      if (item.authenticationEnabled) {
        if (!previousItem.id) {
          // Create a new Samba domain
          actions.push('setSambaServiceUser');
          actions.push('bindSambaServiceUserToLdap');
          actions.push('createSambaDomain');
        } else {
          // Update an existing Samba domain
          if (previousItem.sambaServiceUser
              && item.sambaServiceUser.id !== previousItem.sambaServiceUser.id) {
            actions.push('unsetPreviousSambaServiceUser');
          }

          actions.push('setSambaServiceUser');
          actions.push('bindSambaServiceUserToLdap');
          actions.push('updateSambaDomain');
        }
      } else if (!item.authenticationEnabled && previousItem.authenticationEnabled) {
        // Delete an existing Samba domain
        actions.push('unsetPreviousSambaServiceUser');
        actions.push('deleteSambaDomain');
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const action of actions) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(action, { ldapServerId });
      }
    },

    async setSambaServiceUser({ state }) {
      const { id } = state.item.sambaServiceUser;
      return SystemUserApi.update({ id, samba_service_user: true });
    },

    async unsetPreviousSambaServiceUser({ state }) {
      const { id } = state.previousItem.sambaServiceUser;
      return SystemUserApi.update({ id, samba_service_user: false });
    },

    async bindSambaServiceUserToLdap({ state }, { ldapServerId }) {
      const { id } = state.item.sambaServiceUser;
      const { ldap, systemUser } = ItemTypeConsts;

      const associations = await LdapServerApi.getAssociations(ldapServerId);
      const exists = !!associations.find(association => association.to.id === id);

      if (!exists) {
        await DirectoryGroupingApi.bindAssociation({
          parentType: ldap,
          parentId: ldapServerId,
          operation: 'add',
          targetType: systemUser,
          targetId: id,
        });
      }
    },

    async createSambaDomain({ state }, { ldapServerId }) {
      const { name, sid } = state.item;
      return LdapServerApi.createSambaDomain(ldapServerId, { name, sid });
    },

    async updateSambaDomain({ state }, { ldapServerId }) {
      const { id, name, sid } = state.item;
      return LdapServerApi.updateSambaDomain(ldapServerId, { id, name, sid });
    },

    async deleteSambaDomain({ state }, { ldapServerId }) {
      const { id } = state.previousItem;
      return LdapServerApi.deleteSambaDomain(ldapServerId, { id });
    },
  },

  getters: {
    ...model.getters,
  },

  mutations: {
    ...model.mutations,
  },
};
